<script setup lang="ts">
import { ref } from 'vue';
import { RequestError } from 'Api';

// Store
import { useLogin } from 'Store';

// Auth
import { ILoginData } from 'Auth';

// Components
import BaseForm from 'Components/Base/BaseForm.vue';
import BaseFormInput from 'Components/Base/BaseFormInput.vue';
import { BaseButton } from 'Components/Base/BaseButton';
import BaseFormGroup from 'Components/Base/BaseFormGroup.vue';
import { BaseNotificationInline } from 'Components/Base/BaseNotificationInline';
import AuthSocialButtons from 'Components/AuthSocialButtons/AuthSocialButtons.vue';

// Composables
import { useFormLogin } from './composables/useFormLogin';

// emits
const emit = defineEmits<{ (e: 'success', loginData: ILoginData): void }>();

// props
const props = defineProps<{
  redirectOnSignup?: string;
}>();

// initialization
const {
  FormBeforeSendError,
  $v,
  formModel,
  formSend,
  isFormLoading,
  formSendErrorMessage,
  isFormSendSuccess,
} = useFormLogin();

const passwordType = ref('password');

async function onLoginSuccess(loginData: ILoginData) {
  useLogin()
    .setLoginData(loginData);
  emit('success', loginData);
}

function onSocialSuccess(loginData: ILoginData) {
  // if first login, it needs to complete signup so redirect to sign-up-social which is on vue2
  if (loginData.isFirstLogin) {
    window.location.href = '/sign-up-social';
  } else {
    onLoginSuccess(loginData);
  }
}

function showHidePassword() {
  if (passwordType.value === 'password') {
    passwordType.value = 'text';
  } else {
    passwordType.value = 'password';
  }
}

async function send() {
  try {
    const loginData = await formSend();
    await onLoginSuccess(loginData as ILoginData);
  } catch (error) {
    if (!(error instanceof FormBeforeSendError) && !(error as RequestError).handled) throw error;
  }
}
</script>

<template>
  <div class="FormLogin">
    <div class="form-login__social">
      <AuthSocialButtons
        @success="onSocialSuccess"
      />
    </div>

    <BaseForm
      v-slot="{ isFormSendLoading }"
      keep-loading-send-on-success
      :send-loading="isFormLoading"
      :send-success="isFormSendSuccess"
      class="form-login"
    >
      <div class="form-login__or-arrow">
        <h6 class="form-login__or-arrow-title">or</h6>
      </div>
      <div class="form-login__fields">
        <BaseFormGroup
          v-slot="{ isFieldError }"
          class="form-login__field"
          label="Email"
          :validation="$v.email"
          required
        >
          <BaseFormInput
            v-model="formModel.data.email"
            auto-focused
            class="form-login__input"
            placeholder="Email Address"
            :is-error="isFieldError"
            :disabled="isFormSendLoading"
          />
        </BaseFormGroup>
        <BaseFormGroup
          v-slot="{ isFieldError }"
          class="form-login__field form-login__password-field"
          label="Password"
          :validation="$v.password"
          required
        >
          <BaseFormInput
            v-model="formModel.data.password"
            class="form-login__input"
            placeholder="Password (min. 8 characters)"
            :type="passwordType"
            :disabled="isFormSendLoading"
            :is-error="isFieldError"
            prepend
          >
            <template #prepend>
              <div
                v-if="formModel.data.password"
                class="form-login__password-button"
                @click="showHidePassword"
              >
                <!-- eslint-disable vue/no-v-html -->
                <span
                  v-if="passwordType === 'password'"
                  class="form-login__password-button-icon"
                  v-html="require(`!!raw-loader!images/svg-icons/eye-slash.svg`).default"
                />
                <span
                  v-else
                  class="form-login__password-button-icon"
                  v-html="require(`!!raw-loader!images/svg-icons/eye.svg`).default"
                />
              </div>
            </template>
          </BaseFormInput>
        </BaseFormGroup>
        <span class="form-login__label-link">
          <a
            href="/reset-password"
            class="link-2 small-link"
          >Forgot password</a>
        </span>
        <BaseNotificationInline
          class="form-login__notification"
          :show="Boolean(formSendErrorMessage)"
        >
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span v-html="formSendErrorMessage" />
        </BaseNotificationInline>
        <BaseButton
          type="submit"
          color="primary"
          :loading="isFormSendLoading"
          tabindex="0"
          large
          class="form-login__button"
          @click.prevent="send"
        >
          <span
            class="form-login__envelope-icon svg-icon"
            v-html="require(`!!raw-loader!images/svg-icons/fa-envelope.svg`).default"
          />
          Log In with Email
        </BaseButton>
        <div class="form-login__signup">
          <p class="form-login__signup-text small-text">Don’t have an account?</p>
          <BaseButton
            tag="a"
            color="transparent"
            :href="'/sign-up' + (props.redirectOnSignup ? '?redirect=' + props.redirectOnSignup : '')"
            tabindex="0"
            large
          >
            Sign Up
          </BaseButton>
        </div>
      </div>
    </BaseForm>
  </div>
</template>

<style lang="sass" scoped>
@import '~Styles/_variables'
.form-login
  display: flex
  flex-direction: column

  &__social
    display: flex
    flex-direction: row
    justify-content: center
    align-items: center
    @media screen and (max-width: 576px)
      flex-direction: column
      justify-content: center

  &__or-arrow
    border-bottom: 1px solid $support_lightgray
    position: relative
    margin: 30px 0

  &__or-arrow-title
    position: absolute
    z-index: 2
    left: 50%
    top: 50%
    transform: translate(-50%, -50%)
    background-color: $gray-10
    padding: 0 20px
    min-width: 15px
    color: $text_gray

  &__signup
    display: flex
    align-items: center
    justify-content: center
    margin-top: 12px

  &__signup-text
    color: $text_gray
    margin-bottom: 0
    margin-right: 16px
    +font(400)
  &__fields
    background-color: $white
    padding: 10px
  &__field
    margin-bottom: 21px
  &__password-field
    margin-bottom: 4px
  &__label-link
    line-height: 20px
  &__notification
    margin-top: 12px
  &__password-button
    cursor: pointer
  &__button
    margin-top: 40px
    width: 100%
    @media screen and (max-width: 576px)
      margin-top: 35px
  &__envelope-icon.svg-icon
    margin-right: 12px
    height: initial
    width: initial
    display: flex
    :deep(svg)
      width: inherit
      height: 25px
</style>
